/**
 * Entity property utilities.
 * Provides functions for getting/setting entity properties like color, layer, etc.
 */

import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import type { DrawingEntityProps, DrawingColor } from "../types/NoteConfig";
import { deleteObj } from "../core/delete";
import { openAs } from "../core/oda";
import { OpenMode } from "../core/constants";
import { parseRGBColor, isRGBColor, normalizeColor } from "../types/NoteConfig";
import { resolveLinetype } from "../linetype/utils";
import { setOdColorRgb } from "../colors/color";
import { noteGraphicsModified } from "../telemetry/updateTelemetry";

/**
 * Apply entity properties to an OdDbEntity.
 * This is the main function for styling entities based on NoteConfig.
 */
export function applyEntityProps(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  entityId: DrawingModule.OdDbObjectId,
  props: DrawingEntityProps,
): void {
  const entity = openAs(entityId, module.OdDbEntity, OpenMode.kForWrite);

  try {
    // Apply layer
    if (props.layername) {
      setEntityLayer(module, db, entity, props.layername);
    }

    // Apply color (normalize number[] to string first)
    if (props.color) {
      const normalizedColor = normalizeColor(props.color) as DrawingColor;
      if (normalizedColor) {
        setEntityColor(module, entity, normalizedColor);
      }
    }

    // Apply line weight
    if (props.lineweight !== undefined) {
      entity.setLineWeight(props.lineweight, true);
    }

    // Apply linetype
    if (props.linetype) {
      entity.setLinetype1(resolveLinetype(module, db, props.linetype), true);
    }
  } finally {
    deleteObj(entity);
  }
}

/**
 * Set the layer of an entity by name.
 * Creates the layer if it doesn't exist.
 */
export function setEntityLayer(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  entity: DrawingModule.OdDbEntity,
  layerName: string,
): void {
  // Get or create layer
  const layerTableId = db.getLayerTableId();
  const layerTable = openAs(
    layerTableId,
    module.OdDbLayerTable,
    OpenMode.kForWrite,
  );

  const layerNameStr = new module.OdString();
  layerNameStr.set(layerName);

  let layerId: DrawingModule.OdDbObjectId;

  if (layerTable.has1(layerNameStr)) {
    // Layer exists - get its ID
    layerId = layerTable.getAt3(layerNameStr, false);
  } else {
    // Create new layer
    const newLayer = module.OdDbLayerTableRecord.createObject();
    newLayer.setName(layerNameStr);
    layerId = layerTable.add(newLayer);
    deleteObj(newLayer);
  }

  // Set entity's layer (layerId, doSubents, allowHiddenLayer)
  entity.setLayer1(layerId, true, false);

  deleteObj(layerNameStr);
  deleteObj(layerTable);
}

/**
 * Set the color of an entity.
 */
export function setEntityColor(
  module: DrawingModuleType,
  entity: DrawingModule.OdDbEntity,
  color: DrawingColor,
): void {
  const colorObj = new module.OdCmColor();

  if (color === "kByLayer") {
    colorObj.setColorMethod(192); // ColorMethod.kByLayer
  } else if (color === "kByBlock") {
    colorObj.setColorMethod(193); // ColorMethod.kByBlock
  } else if (isRGBColor(color)) {
    setOdColorRgb(colorObj, parseRGBColor(color));
  }

  entity.setColor(colorObj, true);
  deleteObj(colorObj);
}

/**
 * Get the layer name of an entity.
 */
export function getEntityLayerName(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): string {
  const entity = openAs(entityId, module.OdDbEntity, OpenMode.kForRead);
  const layerId = entity.layerId();

  if (layerId.isNull()) {
    deleteObj(entity);
    return "0"; // Default layer
  }

  const layer = openAs(layerId, module.OdDbLayerTableRecord, OpenMode.kForRead);
  const nameStr = layer.getName();
  const name = nameStr.toString();

  deleteObj(nameStr);
  deleteObj(layer);
  deleteObj(entity);

  return name;
}

/**
 * Records the entity for next Graphic update
 */
export function recordGraphicsModified(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
) {
  const entity = openAs(entityId, module.OdDbEntity, 1);
  entity.recordGraphicsModified(true);
  deleteObj(entity);
  noteGraphicsModified("entity");
}
